import { Component } from '@theme/component';

/**
 * @typedef {Object} PeriodFinderRefs
 * @property {HTMLScriptElement} config
 * @property {HTMLScriptElement} products
 * @property {HTMLElement} mount
 */

/**
 * @typedef {Object} PeriodFinderState
 * @property {number} stepIndex
 * @property {Record<string, string>} answers
 */

/**
 * Swimwear finder quiz.
 *
 * This slice matches the swimwear flow to real products from the configured source collection.
 *
 * @extends {Component<PeriodFinderRefs>}
 */
class PeriodFinder extends Component {
  requiredRefs = ['config', 'products', 'mount'];

  /** @type {Record<string, any>} */
  #config = {};

  /** @type {Record<string, any>[]} */
  #products = [];

  /** @type {PeriodFinderState} */
  #state = this.#initialState();

  /** @type {AbortController | null} */
  #cartController = null;

  #hasCompleted = false;

  connectedCallback() {
    super.connectedCallback();

    this.#hydrate();
    this.#emit('quiz_page_view');
    this.#startQuiz();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#cartController?.abort();
  }

  /**
   * Starts the quiz flow.
   *
   * @param {PointerEvent} event
   */
  handleStart(event) {
    event.preventDefault();

    this.#startQuiz(true);
  }

  /**
   * Captures an answer and advances to the next step.
   *
   * @param {PointerEvent} event
   */
  handleAnswer(event) {
    event.preventDefault();

    const button = event.target instanceof Element ? event.target.closest('button[data-answer-key]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const { answerKey, answerValue } = button.dataset;
    if (!answerKey || !answerValue) return;

    this.#emit('quiz_answer', {
      ...this.#stepPayload(answerKey),
      answer_value: answerValue,
      answer_label: this.#answerLabel(answerKey, answerValue),
    });
    this.#clearAnswersAfter(answerKey);
    this.#state.answers[answerKey] = answerValue;
    this.#state.stepIndex = Math.min(this.#state.stepIndex + 1, this.#steps().length - 1);
    this.#hasCompleted = false;

    this.#renderStep(true);
    this.#emitStepView();
  }

  /**
   * Returns to the previous quiz step.
   *
   * @param {PointerEvent} event
   */
  handleBack(event) {
    event.preventDefault();

    const fromStepIndex = this.#state.stepIndex;
    const fromStepName = this.#currentStep();

    this.#state.stepIndex = Math.max(this.#state.stepIndex - 1, 0);

    this.#emit('quiz_back_click', {
      from_step_index: fromStepIndex + 1,
      from_step_name: fromStepName,
      from_step_label: this.#stepHeading(fromStepName),
      to_step_index: this.#state.stepIndex + 1,
      to_step_name: this.#currentStep(),
      to_step_label: this.#stepHeading(this.#currentStep()),
    });
    this.#renderStep(true);
    this.#emitStepView();
  }

  /**
   * Updates the selected colour/product sibling for one recommendation card.
   *
   * @param {PointerEvent} event
   */
  handleSwatch(event) {
    event.preventDefault();

    const button = event.target instanceof Element ? event.target.closest('button[data-product-handle]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const { productHandle, slotKey } = button.dataset;
    if (!productHandle || !slotKey) return;

    this.#state.answers[`selectedHandle:${slotKey}`] = productHandle;
    this.#emit('quiz_swatch_select', {
      slot_key: slotKey,
      product_handle: productHandle,
      color_name: button.getAttribute('aria-label') || '',
    });
    this.#renderResults();
  }

  /**
   * Handles result CTA clicks so analytics and cart attribution run before navigation.
   *
   * @param {MouseEvent} event
   */
  async handleCtaClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[data-product-handle]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const { productHandle, variantId, price, tier } = link.dataset;
    this.#emit('quiz_product_click', {
      product_handle: productHandle,
      price,
      tier,
      variant_id: variantId,
    });
    this.#emitSelectItem(productHandle || '', variantId || '', tier || '');

    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === '_blank') return;

    event.preventDefault();

    await this.#setCartAttributes(productHandle || '');
    window.location.href = link.href;
  }

  /**
   * Tracks clicks to the swim collection from quiz results.
   *
   * @param {MouseEvent} event
   */
  handleCollectionClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    this.#emit('quiz_collection_click', {
      collection_url: link.pathname + link.search + link.hash,
    });
  }

  /**
   * Restarts the quiz at the first question.
   *
   * @param {PointerEvent} event
   */
  handleRestart(event) {
    event.preventDefault();

    this.#emit('quiz_restart_click', {
      completed: this.#hasCompleted,
      current_step_index: this.#state.stepIndex + 1,
      current_step_name: this.#currentStep(),
      current_step_label: this.#stepHeading(this.#currentStep()),
    });
    this.#startQuiz(true);
  }

  #hydrate() {
    this.#config = this.#parseJson(this.refs.config, {});

    const products = this.#parseJson(this.refs.products, []);
    this.#products = Array.isArray(products) ? products : [];
  }

  /**
   * @returns {boolean}
   */
  #canStart() {
    return Boolean(this.#config.hasSwimCollection && this.#products.length > 0);
  }

  /**
   * @param {boolean} [focusHeading]
   */
  #startQuiz(focusHeading = false) {
    this.#state = this.#initialState();
    this.#hasCompleted = false;

    if (!this.#canStart()) {
      this.classList.remove('period-finder__component--active');
      this.#renderIntro();
      return;
    }

    this.classList.add('period-finder__component--active');
    this.#emit('quiz_start');
    this.#renderStep(focusHeading);
    this.#emitStepView();
  }

  #renderIntro() {
    const validationItems = this.#validationItems();

    this.#setContent(
      `<div class="period-finder__validation" aria-labelledby="PeriodFinderValidation-${this.dataset.quizId}">
        <h2 class="period-finder__validation-heading" id="PeriodFinderValidation-${this.dataset.quizId}">
          ${this.#escapeHtml(this.#string('validationHeading'))}
        </h2>
        <ul class="period-finder__validation-list">
          ${validationItems
            .map((item) => `<li class="period-finder__validation-item">${this.#escapeHtml(item)}</li>`)
            .join('')}
        </ul>
      </div>
      ${this.#renderDiagnostics()}`
    );
  }

  /**
   * @returns {string[]}
   */
  #validationItems() {
    const items = [];

    if (this.#config.hasSwimCollection) {
      items.push(this.#string('dataReady'));
      items.push(this.#formatProductCount());

      return items;
    }

    items.push(this.#string('noCollection'));

    return items;
  }

  /**
   * @returns {string}
   */
  #renderDiagnostics() {
    if (!this.#config.designMode || !this.#products.length) return '';

    const missingItems = this.#diagnosticMissingItems();
    const coverageItems = this.#diagnosticCoverageItems();
    const missingMarkup = missingItems.length
      ? `<ul class="period-finder__diagnostic-list">
          ${missingItems.map((item) => `<li class="period-finder__diagnostic-item period-finder__diagnostic-item--warning">${this.#escapeHtml(item)}</li>`).join('')}
        </ul>`
      : `<p class="period-finder__diagnostic-ok">${this.#escapeHtml(this.#string('diagnosticsNoIssues'))}</p>`;

    return `<details class="period-finder__diagnostics">
      <summary class="period-finder__diagnostics-summary">${this.#escapeHtml(this.#string('diagnosticsTitle'))}</summary>
      <div class="period-finder__diagnostics-body">
        <div>
          <h3 class="period-finder__diagnostics-heading">${this.#escapeHtml(this.#string('diagnosticsMissingHeading'))}</h3>
          ${missingMarkup}
        </div>
        <div>
          <h3 class="period-finder__diagnostics-heading">${this.#escapeHtml(this.#string('diagnosticsCoverageHeading'))}</h3>
          <ul class="period-finder__diagnostic-list">
            ${coverageItems
              .map(
                (item) =>
                  `<li class="period-finder__diagnostic-item${item.matched ? ' period-finder__diagnostic-item--ok' : ' period-finder__diagnostic-item--warning'}">
                    <span>${this.#escapeHtml(item.label)}</span>
                    <span>${this.#escapeHtml(item.matched ? this.#string('diagnosticOk') : this.#string('diagnosticMissing'))}</span>
                  </li>`
              )
              .join('')}
          </ul>
        </div>
      </div>
    </details>`;
  }

  /**
   * @returns {string[]}
   */
  #diagnosticMissingItems() {
    const items = [];

    for (const product of this.#products) {
      const missingFields = [];
      if (!this.#field(product, 'ageGroup')) missingFields.push('custom.age_group');
      if (!this.#field(product, 'style')) missingFields.push('custom.filter_style');
      if (!this.#field(product, 'absorbency')) missingFields.push('custom.product_absorbency');
      if (!product.featuredImage?.url) missingFields.push('featured image');
      if (!this.#firstAvailableVariant(product)) missingFields.push('available variant');

      if (missingFields.length) items.push(`${product.title}: ${missingFields.join(', ')}`);
    }

    return items;
  }

  /**
   * @returns {{ label: string, matched: boolean }[]}
   */
  #diagnosticCoverageItems() {
    const activities = ['leisure', 'competition'];
    const configuredStyles = ['bikini', 'racerback', 'scoopback', 'shorts', 'highwaist', 'legsuit'].filter((style) =>
      this.#hasOptionImage('swimStyle', style)
    );
    const stylesByAudience = {
      me: configuredStyles,
      teen: configuredStyles.filter((style) => style !== 'highwaist'),
    };
    const paths = [];

    for (const [audience, styles] of Object.entries(stylesByAudience)) {
      for (const activity of activities) {
        for (const style of styles) paths.push([audience, activity, style]);
      }
    }

    return paths.map(([audience, activity, style]) => ({
      label: `${this.#audienceLabel(audience)} · ${this.#activityLabel(activity)} · ${this.#styleLabel(style)}`,
      matched: this.#products.some(
        (product) =>
          product.available &&
          this.#productMatchesAudience(product, audience) &&
          this.#productMatchesActivity(product, activity) &&
          this.#productMatchesSwim(product) &&
          this.#productMatchesStyle(product, style)
      ),
    }));
  }

  /**
   * @param {boolean} [focusHeading]
   */
  #renderStep(focusHeading = false) {
    const step = this.#currentStep();

    if (step === 'results') {
      this.#renderResults(focusHeading);
      return;
    }

    this.#setContent(
      `${this.#renderProgress()}
      <div class="period-finder__step">
        ${this.#renderStepCopy(step)}
        ${this.#renderOptions(step)}
        ${this.#renderNavigation()}
      </div>`,
      focusHeading
    );
  }

  /**
   * @param {boolean} [focusHeading]
   */
  #renderResults(focusHeading = false) {
    const match = this.#matchSwimProduct();

    if (!match) {
      this.#renderNoMatch(focusHeading);
      return;
    }

    const { product } = match;
    const slots = this.#recommendationSlots(match);
    const collectionLink = this.#renderCollectionLink();
    const variant = this.#slotVariant(slots[0]);

    if (!this.#hasCompleted) {
      this.#emitComplete(product, variant, slots);
      this.#emitViewItemList(slots);
      this.#hasCompleted = true;
    }

    this.#setContent(
      `${this.#renderProgress()}
      <div class="period-finder__step period-finder__results">
        <div class="period-finder__result-header">
          <div class="period-finder__result-badge" aria-hidden="true">✓ ${this.#escapeHtml(this.#string('recommendationBadge'))}</div>
          <h2 class="period-finder__result-title period-finder__step-heading" tabindex="-1">${this.#escapeHtml(this.#resultTitle())}</h2>
          <p class="period-finder__result-desc">${this.#escapeHtml(this.#string('resultsText'))}</p>
          <p class="period-finder__why"><strong>${this.#escapeHtml(this.#string('whyMatch'))}:</strong> ${this.#escapeHtml(this.#whyMatchText())}.</p>
          ${this.#renderResultCallout()}
        </div>
        ${this.#renderRecommendationGrid(slots)}
        ${this.#renderResultTrust()}
        ${this.#renderResultActions(collectionLink)}
      </div>`,
      focusHeading
    );
  }

  /**
   * @param {boolean} [focusHeading]
   */
  #renderNoMatch(focusHeading = false) {
    this.#setContent(
      `${this.#renderProgress()}
      <div class="period-finder__step period-finder__results">
        <h2 class="period-finder__step-heading" tabindex="-1">${this.#escapeHtml(this.#string('noMatchHeading'))}</h2>
        <p class="period-finder__step-text">${this.#escapeHtml(this.#string('noMatchText'))}</p>
        <div class="period-finder__summary" aria-label="${this.#escapeAttribute(this.#string('summaryLabel'))}">
          ${this.#summaryItems().map((item) => `<span class="period-finder__summary-pill">${this.#escapeHtml(item)}</span>`).join('')}
        </div>
        ${this.#renderResultActions(this.#renderCollectionLink())}
      </div>`,
      focusHeading
    );
  }

  /**
   * @returns {string}
   */
  #renderResultCallout() {
    const messages = [];

    if (this.#state.answers.who === 'teen') {
      if (this.#state.answers.teenAge === 'young') messages.push(this.#string('teenYoungResultText'));
      if (this.#state.answers.teenAge === 'older') messages.push(this.#string('teenOlderResultText'));
      if (this.#state.answers.teenExperience === 'new') messages.push(this.#string('teenExperienceNewResultText'));
      if (this.#state.answers.teenExperience === 'tried') messages.push(this.#string('teenExperienceTriedResultText'));
      if (this.#state.answers.teenExperience === 'loved') messages.push(this.#string('teenExperienceLovedResultText'));
    }

    const text = messages.filter(Boolean).join(' ');
    if (!text) return '';

    return `<div class="period-finder__result-callout">
      <span class="period-finder__result-callout-mark" aria-hidden="true">✓</span>
      <span>${this.#escapeHtml(text)}</span>
    </div>`;
  }

  /**
   * @returns {string}
   */
  #resultTitle() {
    if (this.#state.answers.who === 'teen') return this.#string('resultsTeenHeading') || this.#string('resultsHeading');

    return this.#string('resultsHeading');
  }

  /**
   * @returns {string}
   */
  #whyMatchText() {
    const parts = [];
    const activity = this.#state.answers.swimActivity;
    const style = this.#state.answers.swimStyle;

    if (activity) parts.push(this.#answerLabel('swimActivity', activity).toLowerCase());
    if (style) parts.push(this.#answerLabel('swimStyle', style).toLowerCase());

    return this.#string('whyMatchText').replace('[[answers]]', parts.join(', ') || this.#string('whyMatchFallback'));
  }

  /**
   * @returns {string}
   */
  #swimTrustText() {
    return [this.#string('trustBuiltIn'), this.#string('trustUv'), this.#string('trustChlorine')].filter(Boolean).join(' · ');
  }

  /**
   * @returns {string}
   */
  #renderResultTrust() {
    return `<div class="period-finder__result-trust" aria-label="${this.#escapeAttribute(this.#string('summaryLabel'))}">
      <span>${this.#escapeHtml(this.#string('trustGuarantee'))}</span>
      <span>${this.#escapeHtml(this.#string('trustShipping'))}</span>
    </div>`;
  }

  /**
   * @param {{ product: Record<string, any>, swatches: Record<string, any>[] }} match
   * @returns {{ key: string, baseHandle: string, product: Record<string, any>, variant: Record<string, any> | null, label: string, tier: string, featured: boolean, swatches: Record<string, any>[] }[]}
   */
  #recommendationSlots(match) {
    const crossSell = this.#crossSellSlot();
    const alternativeProducts = match.swatches.filter((product) => product.handle !== match.product.handle);
    const slots = [
      this.#recommendationSlot({
        key: 'featured',
        product: this.#baseProductForSlot('featured', match.product, match.swatches),
        label: this.#string('topPick'),
        tier: 'featured',
        featured: true,
      }),
    ];
    const alternativeCount = crossSell ? 1 : 2;

    for (let index = 0; index < alternativeCount; index += 1) {
      const product = alternativeProducts[index];
      if (!product) break;

      const key = `alternative-${index + 1}`;
      slots.push(
        this.#recommendationSlot({
          key,
          product: this.#baseProductForSlot(key, product, alternativeProducts),
          label: this.#string('recommendationAlternativeLabel'),
          tier: 'alternative',
          featured: false,
        })
      );
    }

    if (crossSell && !slots.some((slot) => slot.baseHandle === crossSell.baseHandle)) slots.push(crossSell);

    const fallbackProducts = this.#fallbackRecommendationProducts(slots.map((slot) => slot.baseHandle));
    for (let index = 0; index < fallbackProducts.length; index += 1) {
      if (slots.length >= 3) break;

      const key = `also-consider-${index + 1}`;
      slots.push(
        this.#recommendationSlot({
          key,
          product: this.#baseProductForSlot(key, fallbackProducts[index], fallbackProducts),
          label: this.#string('recommendationConsiderLabel'),
          tier: 'also_consider',
          featured: false,
        })
      );
    }

    return slots.slice(0, 3);
  }

  /**
   * @param {{ key: string, product: Record<string, any>, label: string, tier: string, featured: boolean, variant?: Record<string, any> | null, swatches?: Record<string, any>[] }} slot
   * @returns {{ key: string, baseHandle: string, product: Record<string, any>, variant: Record<string, any> | null, label: string, tier: string, featured: boolean, swatches: Record<string, any>[] }}
   */
  #recommendationSlot(slot) {
    const baseHandle = slot.product.handle;
    const swatches = slot.swatches || this.#swatchProducts(slot.product);
    const product = this.#selectedProductForSlot(slot.key, slot.product, swatches);
    const variant = slot.variant && product.handle === slot.product.handle ? slot.variant : this.#firstAvailableVariant(product);

    return {
      key: slot.key,
      baseHandle,
      product,
      variant,
      label: slot.label,
      tier: slot.tier,
      featured: slot.featured,
      swatches,
    };
  }

  /**
   * @param {string} key
   * @param {Record<string, any>} fallbackProduct
   * @param {Record<string, any>[]} candidates
   * @returns {Record<string, any>}
   */
  #baseProductForSlot(key, fallbackProduct, candidates) {
    const baseKey = `slotBase:${key}`;
    const baseProduct = candidates.find((product) => product.handle === this.#state.answers[baseKey]);
    if (baseProduct) return baseProduct;

    this.#state.answers[baseKey] = fallbackProduct.handle;

    return fallbackProduct;
  }

  /**
   * @param {string} key
   * @param {Record<string, any>} fallbackProduct
   * @param {Record<string, any>[]} swatches
   * @returns {Record<string, any>}
   */
  #selectedProductForSlot(key, fallbackProduct, swatches) {
    const selectedHandle = this.#state.answers[`selectedHandle:${key}`];

    return swatches.find((product) => product.handle === selectedHandle) || fallbackProduct;
  }

  /**
   * @returns {{ key: string, baseHandle: string, product: Record<string, any>, variant: Record<string, any> | null, label: string, tier: string, featured: boolean, swatches: Record<string, any>[] } | null}
   */
  #crossSellSlot() {
    const crossSell = this.#config.swimHighWaistCrossSell;
    if (this.#state.answers.swimStyle !== 'highwaist' || !crossSell) return null;

    return this.#recommendationSlot({
      key: 'cross-sell',
      product: crossSell,
      variant: crossSell.variant || null,
      swatches: [crossSell],
      label: this.#string('crossSellHeading'),
      tier: 'cross_sell',
      featured: false,
    });
  }

  /**
   * @param {string[]} excludedHandles
   * @returns {Record<string, any>[]}
   */
  #fallbackRecommendationProducts(excludedHandles) {
    const activity = this.#state.answers.swimActivity;
    const audience = this.#state.answers.who;
    const excluded = new Set(excludedHandles);
    const eligibleProducts = this.#products.filter(
      (product) =>
        product.available &&
        !excluded.has(product.handle) &&
        this.#productMatchesAudience(product, audience) &&
        this.#productMatchesSwim(product)
    );
    const styleProducts = eligibleProducts.filter((product) => this.#productMatchesStyle(product, this.#state.answers.swimStyle));
    const styleActivityProducts = activity ? styleProducts.filter((product) => this.#productMatchesActivity(product, activity)) : styleProducts;
    const activityProducts = activity ? eligibleProducts.filter((product) => this.#productMatchesActivity(product, activity)) : eligibleProducts;
    const candidates = styleActivityProducts.length
      ? styleActivityProducts
      : styleProducts.length
        ? styleProducts
        : activityProducts.length
          ? activityProducts
          : eligibleProducts;

    return candidates.sort((product, nextProduct) => {
      const productStyleMatch = this.#productMatchesStyle(product, this.#state.answers.swimStyle) ? 1 : 0;
      const nextProductStyleMatch = this.#productMatchesStyle(nextProduct, this.#state.answers.swimStyle) ? 1 : 0;
      const productActivityMatch = this.#productMatchesActivity(product, activity) ? 1 : 0;
      const nextProductActivityMatch = this.#productMatchesActivity(nextProduct, activity) ? 1 : 0;
      const productBoost = this.#field(product, 'quizBoost') ? 1 : 0;
      const nextProductBoost = this.#field(nextProduct, 'quizBoost') ? 1 : 0;

      return nextProductStyleMatch - productStyleMatch || nextProductActivityMatch - productActivityMatch || nextProductBoost - productBoost;
    });
  }

  /**
   * @param {{ key: string, product: Record<string, any>, variant: Record<string, any> | null, label: string, tier: string, featured: boolean, swatches: Record<string, any>[] }[]} slots
   * @returns {string}
   */
  #renderRecommendationGrid(slots) {
    return `<div class="period-finder__recommendations period-finder__recommendations--count-${slots.length}">
      ${slots.map((slot, index) => this.#renderRecommendationCard(slot, index)).join('')}
    </div>`;
  }

  /**
   * @param {{ key: string, product: Record<string, any>, variant: Record<string, any> | null, label: string, tier: string, featured: boolean, swatches: Record<string, any>[] }} slot
   * @param {number} index
   * @returns {string}
   */
  #renderRecommendationCard(slot, index) {
    const { key, product, variant, label, tier, featured, swatches } = slot;
    const image = product.featuredImage;
    const absorbency = this.#field(product, 'absorbency');
    const absorbencyLabel = absorbency ? this.#string('absorbencyProtection').replace('[[absorbency]]', absorbency) : '';
    const cardClass = featured ? ' period-finder__recommendation-card--featured' : '';
    const titleId = `PeriodFinderRecommendation-${key}`;

    return `<article class="period-finder__recommendation-card${cardClass}" aria-labelledby="${this.#escapeAttribute(titleId)}" data-slot-key="${this.#escapeAttribute(key)}">
      <div class="period-finder__recommendation-label">${this.#escapeHtml(label)}</div>
      <div class="period-finder__recommendation-image">
        ${image?.url ? `<img src="${this.#escapeAttribute(image.url)}" alt="${this.#escapeAttribute(image.alt || product.title)}" loading="lazy" width="${this.#escapeAttribute(image.width || 800)}" height="${this.#escapeAttribute(image.height || 800)}">` : ''}
      </div>
      <div class="period-finder__recommendation-body">
        <h3 class="period-finder__recommendation-title" id="${this.#escapeAttribute(titleId)}">${this.#escapeHtml(product.title)}</h3>
        ${this.#renderRecommendationPrice(variant)}
        <div class="period-finder__recommendation-meta">
          ${absorbencyLabel ? `<span class="period-finder__recommendation-chip period-finder__recommendation-chip--accent">${this.#escapeHtml(absorbencyLabel)}</span>` : ''}
          ${this.#productUsps(product)
            .map((usp) => `<span class="period-finder__recommendation-chip">${this.#escapeHtml(usp)}</span>`)
            .join('')}
        </div>
      </div>
      ${this.#renderSwatches(product, swatches, key, !featured)}
      <a
        class="period-finder__recommendation-cta"
        href="${this.#escapeAttribute(this.#productUrl(product, variant, tier, index))}"
        data-product-handle="${this.#escapeAttribute(product.handle)}"
        data-variant-id="${this.#escapeAttribute(variant?.id || '')}"
        data-price="${this.#escapeAttribute(variant?.price || '')}"
        data-tier="${this.#escapeAttribute(tier)}"
        data-item-index="${this.#escapeAttribute(index)}"
        on:click="/handleCtaClick"
      >
        <span>${this.#escapeHtml(this.#string('shopNow'))}</span>
        ${this.#icon('arrowRight')}
      </a>
    </article>`;
  }

  /**
   * @param {Record<string, any> | null} variant
   * @returns {string}
   */
  #renderRecommendationPrice(variant) {
    if (!variant?.priceMoney) return '';

    const hasCompareAtPrice = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);

    return `<div class="period-finder__recommendation-price-stack">
      <span class="period-finder__recommendation-price">${this.#escapeHtml(this.#stripHtml(variant.priceMoney))}</span>
      ${hasCompareAtPrice ? `<s class="period-finder__recommendation-compare">${this.#escapeHtml(this.#stripHtml(variant.compareAtPriceMoney || ''))}</s>` : ''}
    </div>`;
  }

  /**
   * @param {{ variant?: Record<string, any> | null } | undefined} slot
   * @returns {Record<string, any> | null}
   */
  #slotVariant(slot) {
    return slot?.variant || null;
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any>[]} swatches
   * @returns {string}
   */
  #renderMatchCard(product, swatches) {
    const variant = this.#firstAvailableVariant(product);
    const image = product.featuredImage;
    const absorbency = this.#field(product, 'absorbency');
    const absorbencyLabel = absorbency
      ? this.#string('absorbencyProtection').replace('[[absorbency]]', absorbency)
      : '';
    const uspChips = this.#productUsps(product)
      .map((usp) => `<span class="period-finder__match-chip">${this.#escapeHtml(usp)}</span>`)
      .join('');

    return `<article class="period-finder__match" aria-labelledby="PeriodFinderMatch-${this.#escapeAttribute(product.handle)}">
      <div class="period-finder__match-badge">${this.#escapeHtml(this.#string('topPick'))}</div>
      <div class="period-finder__match-body">
        <div class="period-finder__match-image">
          ${
            image?.url
              ? `<img src="${this.#escapeAttribute(image.url)}" alt="${this.#escapeAttribute(image.alt || product.title)}" loading="lazy" width="${this.#escapeAttribute(image.width || 800)}" height="${this.#escapeAttribute(image.height || 800)}">`
              : ''
          }
        </div>
        <div class="period-finder__match-content">
          <h3 class="period-finder__match-title" id="PeriodFinderMatch-${this.#escapeAttribute(product.handle)}">
            ${this.#escapeHtml(product.title)}
          </h3>
          ${this.#renderPrice(variant)}
          <div class="period-finder__match-meta">
            ${absorbencyLabel ? `<span class="period-finder__match-chip period-finder__match-chip--accent">${this.#escapeHtml(absorbencyLabel)}</span>` : ''}
            <span class="period-finder__match-chip">${this.#escapeHtml(this.#answerLabel('swimStyle', this.#state.answers.swimStyle))}</span>
            ${uspChips}
          </div>
          ${this.#renderSwatches(product, swatches, 'featured')}
          <p class="period-finder__why"><strong>${this.#escapeHtml(this.#string('whyMatch'))}:</strong> ${this.#escapeHtml(this.#summaryItems().join(', '))}.</p>
          <div class="period-finder__trust-strip" aria-label="${this.#escapeAttribute(this.#string('summaryLabel'))}">
            <span>${this.#escapeHtml(this.#string('trustBuiltIn'))}</span>
            <span>${this.#escapeHtml(this.#string('trustUv'))}</span>
            <span>${this.#escapeHtml(this.#string('trustChlorine'))}</span>
          </div>
          ${this.#renderCrossSell()}
          <a
            class="button period-finder__match-cta"
            href="${this.#escapeAttribute(this.#productUrl(product, variant, 'featured'))}"
            data-product-handle="${this.#escapeAttribute(product.handle)}"
            data-variant-id="${this.#escapeAttribute(variant?.id || '')}"
            data-price="${this.#escapeAttribute(variant?.price || '')}"
            data-tier="featured"
            on:click="/handleCtaClick"
          >
            <span>${this.#escapeHtml(this.#string('shopNow'))}</span>
            ${this.#icon('arrowRight')}
          </a>
        </div>
      </div>
    </article>`;
  }

  /**
   * @returns {string}
   */
  #renderCrossSell() {
    const crossSell = this.#config.swimHighWaistCrossSell;
    if (this.#state.answers.swimStyle !== 'highwaist' || !crossSell) return '';

    const variant = crossSell.variant;
    const image = crossSell.featuredImage;

    return `<aside class="period-finder__cross-sell" aria-label="${this.#escapeAttribute(this.#string('crossSellHeading'))}">
      ${image?.url ? `<img src="${this.#escapeAttribute(image.url)}" alt="${this.#escapeAttribute(image.alt || crossSell.title)}" loading="lazy" width="${this.#escapeAttribute(image.width || 300)}" height="${this.#escapeAttribute(image.height || 300)}">` : ''}
      <div class="period-finder__cross-sell-content">
        <div class="period-finder__cross-sell-heading">${this.#escapeHtml(this.#string('crossSellHeading'))}</div>
        <div class="period-finder__cross-sell-title">${this.#escapeHtml(crossSell.title)}</div>
        <p class="period-finder__cross-sell-text">${this.#escapeHtml(this.#string('crossSellText'))}</p>
      </div>
      <a
        class="button button-secondary period-finder__cross-sell-cta"
        href="${this.#escapeAttribute(this.#productUrl(crossSell, variant, 'cross_sell'))}"
        data-product-handle="${this.#escapeAttribute(crossSell.handle)}"
        data-variant-id="${this.#escapeAttribute(variant?.id || '')}"
        data-price="${this.#escapeAttribute(variant?.price || '')}"
        data-tier="cross_sell"
        on:click="/handleCtaClick"
      >
        ${this.#escapeHtml(variant?.priceMoney ? this.#stripHtml(variant.priceMoney) : this.#string('shopNow'))}
      </a>
    </aside>`;
  }

  /**
   * @param {Record<string, any>} activeProduct
   * @param {Record<string, any>[]} swatches
   * @param {string} slotKey
   * @param {boolean} [compact]
   * @returns {string}
   */
  #renderSwatches(activeProduct, swatches, slotKey, compact = false) {
    if (swatches.length < 2) return '';

    const compactClass = compact ? ' period-finder__swatches--compact' : '';

    return `<div class="period-finder__swatches period-finder__recommendation-swatches${compactClass}" aria-label="${this.#escapeAttribute(this.#string('colorSwatchesLabel'))}">
      <span class="period-finder__swatches-label">${this.#escapeHtml(this.#string('colorSwatchesLabel'))}</span>
      ${swatches
        .map((product) => {
          const isActive = product.handle === activeProduct.handle;
          const colorName = this.#colorName(product);

          return `<button
            class="period-finder__swatch"
            type="button"
            data-product-handle="${this.#escapeAttribute(product.handle)}"
            data-slot-key="${this.#escapeAttribute(slotKey)}"
            aria-label="${this.#escapeAttribute(colorName)}"
            aria-pressed="${isActive}"
            style="--period-finder-swatch-color: ${this.#escapeAttribute(this.#swatchColor(product))}"
            on:click="/handleSwatch"
          ></button>`;
        })
        .join('')}
    </div>`;
  }

  /**
   * @param {Record<string, any> | null} variant
   * @returns {string}
   */
  #renderPrice(variant) {
    if (!variant?.priceMoney) return '';

    const hasCompareAtPrice = Number(variant.compareAtPrice || 0) > Number(variant.price || 0);

    return `<div class="period-finder__match-price-stack">
      <span class="period-finder__match-price">${this.#escapeHtml(this.#stripHtml(variant.priceMoney))}</span>
      ${hasCompareAtPrice ? `<s class="period-finder__match-compare">${this.#escapeHtml(this.#stripHtml(variant.compareAtPriceMoney || ''))}</s>` : ''}
    </div>`;
  }

  /**
   * @param {Record<string, any>} product
   * @returns {string[]}
   */
  #productUsps(product) {
    const usps = this.#field(product, 'uniqueSellingPoints');
    const values = Array.isArray(usps) ? usps : String(usps || '').split(';');

    return values.map((usp) => this.#formatUsp(usp)).filter(Boolean).slice(0, 3);
  }

  /**
   * @param {any} usp
   * @returns {string}
   */
  #formatUsp(usp) {
    return this.#extractText(usp)
      .trim()
      .replace(/-/g, ' ')
      .replace(/\buv\b/i, 'UV')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  /**
   * @param {any} value
   * @returns {string}
   */
  #extractText(value) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (Array.isArray(value)) return value.map((item) => this.#extractText(item)).filter(Boolean).join(' ');
    if (!value || typeof value !== 'object') return '';

    const preferredKeys = ['label', 'name', 'title', 'text', 'value', 'key'];

    for (const key of preferredKeys) {
      const text = this.#extractText(value[key]);
      if (text) return text;
    }

    const systemHandle = this.#extractText(value.system?.handle);
    if (systemHandle) return systemHandle;

    for (const item of Object.values(value)) {
      const text = this.#extractText(item);
      if (text) return text;
    }

    return '';
  }

  /**
   * @returns {string}
   */
  #renderCollectionLink() {
    if (!this.#config.swimCollectionUrl) return '';

    return `<a class="button button-secondary period-finder__collection-link" href="${this.#escapeAttribute(this.#config.swimCollectionUrl)}" on:click="/handleCollectionClick">
      ${this.#icon('grid')}
      <span>${this.#escapeHtml(this.#string('browseCollectionButton'))}</span>
      ${this.#icon('arrowRight')}
    </a>`;
  }

  /**
   * @param {string} collectionLink
   * @returns {string}
   */
  #renderResultActions(collectionLink) {
    return `<div class="period-finder__result-actions">
      ${collectionLink}
      <div class="period-finder__result-action-row">
        <button class="button button-secondary period-finder__result-action" type="button" on:click="/handleBack">
          ${this.#icon('arrowLeft')}
          <span>${this.#escapeHtml(this.#string('backButton'))}</span>
        </button>
        <button class="button button-secondary period-finder__result-action" type="button" on:click="/handleRestart">
          ${this.#icon('restart')}
          <span>${this.#escapeHtml(this.#string('restartButton'))}</span>
        </button>
      </div>
    </div>`;
  }

  /**
   * @param {'arrowLeft' | 'arrowRight' | 'check' | 'grid' | 'heart' | 'people' | 'question' | 'refresh' | 'restart'} name
   * @returns {string}
   */
  #icon(name) {
    const icons = {
      arrowLeft: '<svg class="period-finder__icon period-finder__icon--arrow-left" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M10.8 5.4 4.2 12l6.6 6.6 1.4-1.4L8 13h11.8v-2H8l4.2-4.2-1.4-1.4Z" fill="currentColor"/></svg>',
      arrowRight: '<svg class="period-finder__icon period-finder__icon--arrow-right" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m13.2 5.4 6.6 6.6-6.6 6.6-1.4-1.4L16 13H4.2v-2H16l-4.2-4.2 1.4-1.4Z" fill="currentColor"/></svg>',
      check: '<svg class="period-finder__icon period-finder__icon--check" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="m9.7 16.6-4.3-4.3L4 13.8l5.7 5.7L20.5 8.7 19 7.3l-9.3 9.3Z" fill="currentColor"/></svg>',
      grid: '<svg class="period-finder__icon period-finder__icon--grid" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M4 4h6.5v6.5H4V4Zm9.5 0H20v6.5h-6.5V4ZM4 13.5h6.5V20H4v-6.5Zm9.5 0H20V20h-6.5v-6.5Z" fill="currentColor"/></svg>',
      heart: '<svg class="period-finder__icon period-finder__icon--heart" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M12 20.5s-7.2-4.6-9.5-8.8C.5 8 2.7 4.1 6.8 4.1c2 0 3.6 1 4.5 2.5.9-1.5 2.5-2.5 4.5-2.5 4.1 0 6.3 3.9 4.3 7.6-2.3 4.2-8.1 8.8-8.1 8.8Z" fill="currentColor"/></svg>',
      people: '<svg class="period-finder__icon period-finder__icon--people" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M8.4 11.2a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2Zm7.6.4a3.1 3.1 0 1 1 0-6.2 3.1 3.1 0 0 1 0 6.2ZM2.5 20c.4-3.9 2.6-6.2 5.9-6.2s5.5 2.3 5.9 6.2H2.5Zm10.6-1.7c.9-2.6 2.8-4.1 5.1-4.1 2.4 0 4.1 1.9 4.4 5.8h-6.8c-.4-.7-1.4-1.5-2.7-1.7Z" fill="currentColor"/></svg>',
      question: '<svg class="period-finder__icon period-finder__icon--question" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M11 15.4h2v-.7c0-1 .4-1.5 1.4-2.2 1.2-.8 2.1-1.9 2.1-3.6 0-2.5-1.9-4.1-4.6-4.1-2.5 0-4.4 1.5-4.7 3.8l2 .3c.2-1.4 1.2-2.2 2.7-2.2 1.6 0 2.6.8 2.6 2.2 0 1-.5 1.6-1.6 2.3-1.2.8-1.9 1.7-1.9 3.3v.9Zm1 4.1a1.35 1.35 0 1 0 0-2.7 1.35 1.35 0 0 0 0 2.7Z" fill="currentColor"/></svg>',
      refresh: '<svg class="period-finder__icon period-finder__icon--refresh" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M17.7 7.1A7.9 7.9 0 0 0 4.4 10H2.2a10 10 0 0 1 17-4.4L21 3.8V9h-5.2l1.9-1.9ZM6.3 16.9A7.9 7.9 0 0 0 19.6 14h2.2a10 10 0 0 1-17 4.4L3 20.2V15h5.2l-1.9 1.9Z" fill="currentColor"/></svg>',
      restart: '<svg class="period-finder__icon period-finder__icon--restart" aria-hidden="true" focusable="false" viewBox="0 0 24 24"><path d="M12 5a7 7 0 1 1-6.3 4H3.4A9 9 0 1 0 12 3V.8L7.8 4.9 12 9.2V5Z" fill="currentColor"/></svg>',
    };

    return icons[name] || '';
  }

  /**
   * @returns {string}
   */
  #renderProgress() {
    const steps = this.#progressSteps();
    const currentStep = this.#currentStep();
    const isComplete = currentStep === 'results';
    const current = Math.min(this.#state.stepIndex + 1, steps.length);

    return `<div
      class="period-finder__progress"
      role="progressbar"
      aria-label="${this.#escapeAttribute(this.#string('progressLabel'))}"
      aria-valuemin="1"
      aria-valuemax="${steps.length}"
      aria-valuenow="${current}"
      style="--period-finder-progress-count: ${steps.length}"
    >
      ${steps
        .map(
          (_, index) =>
            `<span class="period-finder__progress-segment${isComplete || index < current - 1 ? ' period-finder__progress-segment--complete' : ''}${!isComplete && index === current - 1 ? ' period-finder__progress-segment--active' : ''}" aria-hidden="true"></span>`
        )
        .join('')}
    </div>`;
  }

  /**
   * @param {string} step
   * @returns {string}
   */
  #renderStepCopy(step) {
    return `<div class="period-finder__social-proof"><span class="period-finder__social-stars" aria-hidden="true">★★★★★</span> ${this.#escapeHtml(this.#string('socialProofText'))}</div>
      ${this.#renderStepChapter(step)}
      <div class="period-finder__step-kicker">${this.#escapeHtml(this.#stepLabel())}</div>
      <h2 class="period-finder__step-heading" tabindex="-1">${this.#escapeHtml(this.#string(`${step}Heading`))}</h2>
      <p class="period-finder__step-text">${this.#escapeHtml(this.#string(`${step}Text`))}</p>`;
  }

  /**
   * @param {string} step
   * @returns {string}
   */
  #renderOptions(step) {
    const options = this.#filterOptionsWithConfiguredImages(step, this.#optionsForStep(step));
    const twoColumnClass = this.#usesTwoColumnOptions(step) ? ' period-finder__options--two' : '';

    return `<div class="period-finder__options${twoColumnClass}">
      ${options.map((option) => this.#renderOption(step, option)).join('')}
    </div>`;
  }

  /**
   * @param {string} step
   * @param {{ value: string, label: string, hint: string, disabled?: boolean }} option
   * @returns {string}
   */
  #renderOption(step, option) {
    const selected = this.#state.answers[step] === option.value;
    const image = this.#optionImage(step, option.value);
    const disabledAttribute = option.disabled ? ' disabled' : '';
    const selectedClass = selected ? ' period-finder__option--selected' : '';
    const imageClass = image ? ' period-finder__option--with-image' : '';
    const toneClass = ` period-finder__option--tone-${this.#optionTone(step)}`;
    const icon = this.#optionIcon(step, option.value);

    return `<button
      class="period-finder__option${selectedClass}${imageClass}${toneClass}"
      type="button"
      data-answer-key="${this.#escapeAttribute(step)}"
      data-answer-value="${this.#escapeAttribute(option.value)}"
      on:click="/handleAnswer"
      ${disabledAttribute}
    >
      ${image ? `<span class="period-finder__option-media" aria-hidden="true"><img src="${this.#escapeAttribute(image.url)}" alt="" role="presentation" loading="lazy" width="${this.#escapeAttribute(image.width || 600)}" height="${this.#escapeAttribute(image.height || 600)}"></span>` : ''}
      ${icon ? `<span class="period-finder__option-icon">${icon}</span>` : ''}
      <span class="period-finder__option-content">
        <span class="period-finder__option-label">${this.#escapeHtml(option.label)}</span>
        <span class="period-finder__option-hint">${this.#escapeHtml(option.hint)}</span>
        ${option.disabled ? `<span class="period-finder__option-badge">${this.#escapeHtml(this.#string('comingSoon'))}</span>` : ''}
      </span>
    </button>`;
  }

  /**
   * @param {string} step
   * @param {string} value
   * @returns {string}
   */
  #optionIcon(step, value) {
    if (step === 'who') {
      if (value === 'me') return this.#icon('heart');
      if (value === 'teen') return this.#icon('people');
    }

    if (step === 'periodProofExperience' || step === 'teenExperience') {
      if (value === 'new') return this.#icon('question');
      if (value === 'tried') return this.#icon('refresh');
      if (value === 'loved') return this.#icon('check');
    }

    return '';
  }

  /**
   * @param {string} step
   * @param {string} value
   * @returns {Record<string, any> | null}
   */
  #optionImage(step, value) {
    const directImage = this.#config.optionImages?.[`${step}.${value}`];
    if (directImage) return directImage;

    const fallbackImageKeys = {
      'teenExperience.tried': 'teenExperience.underwear',
      'teenExperience.loved': 'teenExperience.swimwear',
    };
    const fallbackKey = fallbackImageKeys[`${step}.${value}`];

    return fallbackKey ? this.#config.optionImages?.[fallbackKey] || null : null;
  }

  /**
   * @param {string} step
   * @param {string} value
   * @returns {boolean}
   */
  #hasOptionImage(step, value) {
    return Boolean(this.#optionImage(step, value)?.url);
  }

  /**
   * @param {string} step
   * @param {{ value: string, label: string, hint: string, disabled?: boolean }[]} options
   * @returns {{ value: string, label: string, hint: string, disabled?: boolean }[]}
   */
  #filterOptionsWithConfiguredImages(step, options) {
    if (!this.#requiresConfiguredOptionImage(step)) return options;

    return options.filter((option) => this.#hasOptionImage(step, option.value));
  }

  /**
   * @param {string} step
   * @returns {boolean}
   */
  #requiresConfiguredOptionImage(step) {
    return step === 'swimStyle';
  }

  /**
   * @param {string} step
   * @returns {string}
   */
  #renderStepChapter(step) {
    const chapter = this.#chapterForStep(step);
    if (!chapter) return '';

    return `<div class="period-finder__chapter">${this.#escapeHtml(chapter)}</div>`;
  }

  /**
   * @param {string} step
   * @returns {boolean}
   */
  #usesTwoColumnOptions(step) {
    return ['swimActivity', 'swimStyle'].includes(step);
  }

  /**
   * @param {string} step
   * @returns {string}
   */
  #optionTone(step) {
    if (step === 'teenAge' || step === 'teenExperience') return 'teen';
    if (this.#state.answers.who === 'teen' && step === 'who') return 'teen';
    if (step === 'swimActivity' || step === 'swimStyle') return 'swim';

    return 'adult';
  }

  /**
   * @param {string} step
   * @returns {string}
   */
  #chapterForStep(step) {
    if (this.#state.answers.who === 'teen') {
      if (step === 'teenAge' || step === 'teenExperience') return this.#string('chapterAboutHer');

      return this.#string('chapterWhatSheNeeds');
    }

    if (step === 'periodProofExperience') return this.#string('chapterAboutYou');
    if (step === 'who') return '';

    return this.#string('chapterWhatYouNeed');
  }

  /**
   * @returns {string}
   */
  #renderNavigation() {
    if (this.#state.stepIndex === 0) return '';

    return `<div class="period-finder__actions period-finder__step-actions">
      <button class="button button-secondary period-finder__step-back" type="button" on:click="/handleBack">
        ${this.#icon('arrowLeft')}
        <span>${this.#escapeHtml(this.#string('backButton'))}</span>
      </button>
    </div>`;
  }

  /**
   * @param {string} step
   * @returns {{ value: string, label: string, hint: string, disabled?: boolean }[]}
   */
  #optionsForStep(step) {
    if (step === 'who') {
      return [
        { value: 'me', label: this.#string('whoAdultLabel'), hint: this.#string('whoAdultHint') },
        { value: 'teen', label: this.#string('whoTeenLabel'), hint: this.#string('whoTeenHint') },
      ];
    }

    if (step === 'teenAge') {
      return [
        { value: 'young', label: this.#string('teenAgeYoungLabel'), hint: this.#string('teenAgeYoungHint') },
        { value: 'older', label: this.#string('teenAgeOlderLabel'), hint: this.#string('teenAgeOlderHint') },
      ];
    }

    if (step === 'teenExperience') {
      return [
        { value: 'new', label: this.#string('teenExperienceNewLabel'), hint: this.#string('teenExperienceNewHint') },
        { value: 'tried', label: this.#string('teenExperienceTriedLabel'), hint: this.#string('teenExperienceTriedHint') },
        { value: 'loved', label: this.#string('teenExperienceLovedLabel'), hint: this.#string('teenExperienceLovedHint') },
      ];
    }

    if (step === 'periodProofExperience') {
      return [
        { value: 'new', label: this.#string('periodProofExperienceNewLabel'), hint: this.#string('periodProofExperienceNewHint') },
        { value: 'tried', label: this.#string('periodProofExperienceTriedLabel'), hint: this.#string('periodProofExperienceTriedHint') },
        { value: 'loved', label: this.#string('periodProofExperienceLovedLabel'), hint: this.#string('periodProofExperienceLovedHint') },
      ];
    }

    if (step === 'swimActivity') {
      return [
        { value: 'leisure', label: this.#string('activityLeisureLabel'), hint: this.#string('activityLeisureHint') },
        {
          value: 'competition',
          label: this.#string('activityCompetitionLabel'),
          hint: this.#string('activityCompetitionHint'),
        },
      ];
    }

    if (step === 'swimStyle') {
      const options = [
        { value: 'bikini', label: this.#string('styleBikiniLabel'), hint: this.#string('styleBikiniHint') },
        { value: 'racerback', label: this.#string('styleRacerbackLabel'), hint: this.#string('styleRacerbackHint') },
        { value: 'scoopback', label: this.#string('styleScoopbackLabel'), hint: this.#string('styleScoopbackHint') },
        { value: 'shorts', label: this.#string('styleShortsLabel'), hint: this.#string('styleShortsHint') },
      ];

      if (this.#state.answers.who !== 'teen') {
        options.push({ value: 'highwaist', label: this.#string('styleHighWaistLabel'), hint: this.#string('styleHighWaistHint') });
      }

      options.push({ value: 'legsuit', label: this.#string('styleLegsuitLabel'), hint: this.#string('styleLegsuitHint') });

      return options;
    }

    return [];
  }

  /**
   * @returns {{ product: Record<string, any>, swatches: Record<string, any>[] } | null}
   */
  #matchSwimProduct() {
    const audience = this.#state.answers.who;
    const swimActivity = this.#state.answers.swimActivity;
    const swimStyle = this.#state.answers.swimStyle;
    if (!audience || !swimStyle) return null;

    const baseCandidates = this.#products.filter(
      (product) =>
        product.available &&
        this.#productMatchesAudience(product, audience) &&
        this.#productMatchesSwim(product) &&
        this.#productMatchesStyle(product, swimStyle)
    );
    const activityCandidates = swimActivity ? baseCandidates.filter((product) => this.#productMatchesActivity(product, swimActivity)) : baseCandidates;
    const candidates = activityCandidates.length ? activityCandidates : baseCandidates;

    if (!candidates.length) return null;

    const rankedCandidates = candidates.sort((product, nextProduct) => {
      const productActivityMatch = this.#productMatchesActivity(product, swimActivity) ? 1 : 0;
      const nextProductActivityMatch = this.#productMatchesActivity(nextProduct, swimActivity) ? 1 : 0;
      const productStyleMatch = this.#productMatchesStyle(product, swimStyle) ? 1 : 0;
      const nextProductStyleMatch = this.#productMatchesStyle(nextProduct, swimStyle) ? 1 : 0;
      const productBoost = this.#field(product, 'quizBoost') ? 1 : 0;
      const nextProductBoost = this.#field(nextProduct, 'quizBoost') ? 1 : 0;

      return nextProductActivityMatch - productActivityMatch || nextProductStyleMatch - productStyleMatch || nextProductBoost - productBoost;
    });
    const fallbackProduct = rankedCandidates[0];
    const swatches = this.#swatchProducts(fallbackProduct);

    return { product: fallbackProduct, swatches };
  }

  /**
   * @param {Record<string, any>} baseProduct
   * @returns {Record<string, any>[]}
   */
  #swatchProducts(baseProduct) {
    const swatches = this.#products.filter(
      (product) =>
        product.available &&
        this.#productMatchesAudience(product, this.#state.answers.who) &&
        this.#productMatchesActivity(product, this.#state.answers.swimActivity) &&
        this.#productMatchesStyle(product, this.#state.answers.swimStyle) &&
        this.#sameColorGroup(baseProduct, product)
    );

    return swatches.length ? swatches : [baseProduct];
  }

  /**
   * @param {Record<string, any>} product
   * @param {string} audience
   * @returns {boolean}
   */
  #productMatchesAudience(product, audience) {
    const ageGroup = this.#normalize(this.#field(product, 'ageGroup'));

    if (ageGroup.includes('all ages')) return true;
    if (audience === 'teen') return ageGroup.includes('teen');

    return ageGroup.includes('adult');
  }

  /**
   * @param {Record<string, any>} product
   * @returns {boolean}
   */
  #productMatchesSwim(product) {
    const type = this.#normalize(this.#field(product, 'type'));
    const range = this.#normalize(this.#field(product, 'range'));
    const title = this.#normalize(product.title);

    return type.includes('swim') || range.includes('swim') || title.includes('swim') || this.#isCompetitionProduct(product);
  }

  /**
   * @param {Record<string, any>} product
   * @param {string | undefined} activity
   * @returns {boolean}
   */
  #productMatchesActivity(product, activity) {
    if (!activity) return true;
    if (activity === 'competition') return this.#isCompetitionProduct(product);
    if (activity === 'leisure') return !this.#isCompetitionProduct(product);

    return true;
  }

  /**
   * @param {Record<string, any>} product
   * @returns {boolean}
   */
  #isCompetitionProduct(product) {
    const search = [
      this.#field(product, 'range'),
      this.#field(product, 'type'),
      this.#field(product, 'style'),
      this.#field(product, 'uniqueSellingPoints'),
      product.collections,
      product.tags,
      product.title,
    ]
      .map((value) => this.#normalize(value))
      .join(' ');

    return search.includes('competition') || search.includes('competitive') || search.includes('lunafly') || search.includes('club');
  }

  /**
   * @param {Record<string, any>} product
   * @param {string | undefined} style
   * @returns {boolean}
   */
  #productMatchesStyle(product, style) {
    const search = `${this.#normalize(this.#field(product, 'style'))} ${this.#normalize(product.title)}`;

    switch (style) {
      case 'bikini':
        return search.includes('bikini') && (!search.includes('top') || search.includes('kit'));
      case 'racerback':
        return search.includes('racerback');
      case 'scoopback':
        return search.includes('scoopback') || search.includes('scoop back');
      case 'shorts':
        return search.includes('short');
      case 'highwaist':
        return search.includes('high waist') || search.includes('highwaist');
      case 'legsuit':
        return search.includes('legsuit') || search.includes('leg suit');
      default:
        return false;
    }
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any>} nextProduct
   * @returns {boolean}
   */
  #sameColorGroup(product, nextProduct) {
    if (product.handle === nextProduct.handle) return true;

    const productGroup = this.#normalize(this.#field(product, 'colorProductGroup'));
    const nextProductGroup = this.#normalize(this.#field(nextProduct, 'colorProductGroup'));

    return productGroup.includes(nextProduct.handle) || nextProductGroup.includes(product.handle);
  }

  /**
   * @param {Record<string, any>} product
   * @returns {Record<string, any> | null}
   */
  #firstAvailableVariant(product) {
    if (!Array.isArray(product.variants)) return null;

    return product.variants.find((variant) => variant.available) || product.variants[0] || null;
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any> | null} variant
   * @param {string} [tier]
   * @param {number} [index]
   * @returns {string}
   */
  #productUrl(product, variant, tier = '', index = 0) {
    if (!product.url) return '#';

    const url = new URL(product.url, window.location.origin);
    if (variant?.id) url.searchParams.set('variant', variant.id);

    url.searchParams.set('quiz_id', this.#quizId());
    url.searchParams.set('quiz_reco', product.handle || '');
    url.searchParams.set('quiz_event_prefix', this.#eventPrefix());
    url.searchParams.set('quiz_render_context', this.#renderContext());
    url.searchParams.set('quiz_item_list_id', this.#itemListId());
    url.searchParams.set('quiz_item_list_name', this.#itemListName());
    url.searchParams.set('quiz_item_index', String(index));
    if (tier) url.searchParams.set('quiz_tier', tier);

    return `${url.pathname}${url.search}${url.hash}`;
  }

  /**
   * @param {Record<string, any>} product
   * @returns {string}
   */
  #colorName(product) {
    return this.#field(product, 'colorName') || product.title || '';
  }

  /**
   * @param {Record<string, any>} product
   * @returns {string}
   */
  #swatchColor(product) {
    const colorValue = this.#field(product, 'color');
    if (typeof colorValue === 'string' && (colorValue.startsWith('#') || colorValue.startsWith('linear-gradient'))) {
      return colorValue;
    }

    const colorName = this.#normalize(this.#colorName(product));
    const colorMap = {
      black: '#1d1d1d',
      blue: '#2e6784',
      navy: '#173166',
      pink: '#ff81c2',
      purple: '#7a4f8a',
      green: '#54dab5',
    };

    for (const [name, value] of Object.entries(colorMap)) {
      if (colorName.includes(name)) return value;
    }

    return 'var(--color-background)';
  }

  /**
   * @param {Record<string, any>} product
   * @param {string} key
   * @returns {any}
   */
  #field(product, key) {
    return product.metafields?.[key];
  }

  /**
   * @param {string} productHandle
   */
  async #setCartAttributes(productHandle) {
    if (!productHandle || !this.#config.cartUpdateUrl) return;

    this.#cartController?.abort();
    this.#cartController = new AbortController();

    try {
      await fetch(this.#config.cartUpdateUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          attributes: {
            quiz: this.#quizId(),
            quiz_reco: productHandle,
            quiz_item_list_id: this.#itemListId(),
            quiz_item_list_name: this.#itemListName(),
          },
        }),
        signal: this.#cartController.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;

      console.warn('Period finder cart attribution failed', error);
    }
  }

  /**
   * @param {string} eventName
   * @param {Record<string, any>} [detail]
   */
  #emit(eventName, detail = {}) {
    const prefixedEventName = this.#eventName(eventName);
    const payload = {
      event_base: eventName,
      event_prefix: this.#eventPrefix(),
      event_title: prefixedEventName,
      quiz_id: this.#quizId(),
      flow_preset: this.#config.flowPreset || this.dataset.flowPreset || 'swimwear',
      page_path: window.location.pathname,
      render_context: this.#renderContext(),
      ...detail,
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: prefixedEventName, ...payload });

    const shopifyAnalytics = window.Shopify?.analytics;
    if (typeof shopifyAnalytics?.publish === 'function') {
      shopifyAnalytics.publish(prefixedEventName, payload);
    }

    this.dispatchEvent(new CustomEvent(prefixedEventName, { detail: payload, bubbles: true }));
  }

  #emitStepView() {
    this.#emit('quiz_step_view', {
      ...this.#stepPayload(this.#currentStep()),
      audience: this.#state.answers.who,
      period_proof_experience: this.#state.answers.periodProofExperience,
      teen_age: this.#state.answers.teenAge,
      teen_experience: this.#state.answers.teenExperience,
    });
  }

  /**
   * @param {{ product: Record<string, any>, variant: Record<string, any> | null, tier: string }[]} slots
   */
  #emitViewItemList(slots) {
    this.#emitEcommerceEvent('view_item_list', {
      item_list_id: this.#itemListId(),
      item_list_name: this.#itemListName(),
      items: slots.map((slot, index) => this.#ecommerceItem(slot.product, slot.variant, index, slot.tier)),
    });
  }

  /**
   * @param {string} productHandle
   * @param {string} variantId
   * @param {string} tier
   */
  #emitSelectItem(productHandle, variantId, tier) {
    const match = this.#matchSwimProduct();
    const slots = match ? this.#recommendationSlots(match) : [];
    const slotIndex = slots.findIndex((slot) => slot.product.handle === productHandle || String(slot.variant?.id || '') === String(variantId));
    const slot = slotIndex >= 0 ? slots[slotIndex] : null;
    const product = slot?.product || this.#products.find((candidate) => candidate.handle === productHandle);
    if (!product) return;

    const variant = slot?.variant || product.variants?.find((candidate) => String(candidate.id || '') === String(variantId)) || this.#firstAvailableVariant(product);

    this.#emitEcommerceEvent('select_item', {
      item_list_id: this.#itemListId(),
      item_list_name: this.#itemListName(),
      items: [this.#ecommerceItem(product, variant, slotIndex >= 0 ? slotIndex : 0, slot?.tier || tier)],
    });
  }

  /**
   * @param {string} eventName
   * @param {{ item_list_id: string, item_list_name: string, items: Record<string, any>[] }} ecommerce
   */
  #emitEcommerceEvent(eventName, ecommerce) {
    const payload = {
      event: eventName,
      quiz_id: this.#quizId(),
      flow_preset: this.#config.flowPreset || this.dataset.flowPreset || 'swimwear',
      page_path: window.location.pathname,
      render_context: this.#renderContext(),
      ecommerce: {
        currency: this.#currency(),
        ...ecommerce,
      },
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
    this.#logEcommerceEvent(eventName, payload);
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any> | null} variant
   * @param {number} index
   * @param {string} tier
   * @returns {Record<string, any>}
   */
  #ecommerceItem(product, variant, index, tier) {
    const variantTitle = this.#variantTitle(variant);
    const item = {
      index,
      item_id: this.#formattedItemId(product, variant),
      item_name: product.title || '',
      item_list_id: this.#itemListId(),
      item_list_name: this.#itemListName(),
      price: Number(((Number(variant?.price || 0) || 0) / 100).toFixed(2)),
      quantity: 1,
      product_handle: product.handle || '',
      product_id: String(product.id || ''),
      recommendation_tier: tier || '',
      variant_id: String(variant?.id || ''),
    };

    if (product.productType) item.item_category = product.productType;
    if (product.vendor) item.item_brand = product.vendor;
    if (variant?.sku) item.sku = variant.sku;
    if (variantTitle) item.item_variant = variantTitle;

    return item;
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any> | null} variant
   * @returns {string}
   */
  #formattedItemId(product, variant) {
    const fallbackId = String(variant?.id || product.id || product.handle || '');

    try {
      const countryCode = window.localStorage?.getItem('shopCountryCode');
      if (countryCode && product.id && variant?.id) return `shopify_${countryCode}_${product.id}_${variant.id}`;
    } catch (error) {
      // Keep the fallback id if storage access is blocked.
    }

    return fallbackId;
  }

  /**
   * @param {Record<string, any> | null} variant
   * @returns {string}
   */
  #variantTitle(variant) {
    const title = variant?.title || (Array.isArray(variant?.options) ? variant.options.filter(Boolean).join(' / ') : '');

    return title && title !== 'Default Title' ? title : '';
  }

  /**
   * @param {string} eventName
   * @param {Record<string, any>} payload
   */
  #logEcommerceEvent(eventName, payload) {
    const label = `[WUKA Quiz GA4] ${eventName}`;

    if (typeof console.groupCollapsed === 'function') {
      console.groupCollapsed(label);
      console.log(payload);
      console.groupEnd();
      return;
    }

    console.log(label, payload);
  }

  /**
   * @param {Record<string, any>} product
   * @param {Record<string, any> | null} variant
   * @param {{ product: Record<string, any> }[]} [slots]
   */
  #emitComplete(product, variant, slots = []) {
    const recommendationHandles = slots.map((slot) => slot.product.handle);

    this.#emit('quiz_complete', {
      audience: this.#state.answers.who,
      period_proof_experience: this.#state.answers.periodProofExperience,
      primary_recommendation_handle: product.handle,
      recommendation_handle: product.handle,
      recommendation_handles: recommendationHandles,
      result_count: recommendationHandles.length,
      situation: 'swim',
      style: this.#state.answers.swimStyle,
      swim_activity: this.#state.answers.swimActivity,
      teen_age: this.#state.answers.teenAge,
      teen_experience: this.#state.answers.teenExperience,
      variant_id: variant?.id,
    });
  }

  /**
   * @returns {string}
   */
  #quizId() {
    return this.#config.quizId || this.dataset.quizId || 'swimwear-product-quiz';
  }

  /**
   * @returns {string}
   */
  #currency() {
    return this.#config.currency || 'GBP';
  }

  /**
   * @returns {string}
   */
  #itemListId() {
    return `${this.#quizId()}`
      .toLowerCase()
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '')
      .concat('_results');
  }

  /**
   * @returns {string}
   */
  #itemListName() {
    return 'Swimwear quiz results';
  }

  /**
   * @returns {string}
   */
  #eventPrefix() {
    return this.#normalizeEventPrefix(this.#config.analyticsEventPrefix || 'swimwear_quiz_');
  }

  /**
   * @param {string} eventName
   * @returns {string}
   */
  #eventName(eventName) {
    const baseEventName = String(eventName || '').replace(/^quiz_/, '');

    return `${this.#eventPrefix()}${baseEventName}`;
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeEventPrefix(value) {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_+|_+$/g, '');

    return normalized ? `${normalized}_` : '';
  }

  /**
   * @returns {string}
   */
  #renderContext() {
    return this.closest('dialog') ? 'modal' : 'page';
  }

  /**
   * @param {string} step
   * @returns {Record<string, any>}
   */
  #stepPayload(step) {
    const steps = this.#steps();
    const stepIndex = steps.indexOf(step);

    return {
      step_count: this.#progressSteps().length,
      step_index: stepIndex >= 0 ? stepIndex + 1 : this.#state.stepIndex + 1,
      step_label: this.#stepHeading(step),
      step_name: step,
    };
  }

  /**
   * @param {string} step
   * @returns {string}
   */
  #stepHeading(step) {
    if (step === 'results') return this.#string('resultsHeading');

    return this.#string(`${step}Heading`);
  }

  /**
   * @returns {PeriodFinderState}
   */
  #initialState() {
    return {
      stepIndex: 0,
      answers: { situation: 'swim' },
    };
  }

  /**
   * @returns {string[]}
   */
  #steps() {
    if (this.#state.answers.who === 'teen') return ['who', 'teenAge', 'teenExperience', 'swimActivity', 'swimStyle', 'results'];

    return ['who', 'periodProofExperience', 'swimActivity', 'swimStyle', 'results'];
  }

  /**
   * @returns {string[]}
   */
  #progressSteps() {
    return this.#steps().filter((step) => step !== 'results');
  }

  /**
   * @returns {string}
   */
  #currentStep() {
    return this.#steps()[this.#state.stepIndex] || 'who';
  }

  /**
   * @returns {string}
   */
  #stepLabel() {
    return this.#string('stepLabel')
      .replace('[[current]]', (this.#state.stepIndex + 1).toString())
      .replace('[[total]]', (this.#steps().length - 1).toString());
  }

  /**
   * @returns {string[]}
   */
  #summaryItems() {
    return [
      this.#answerLabel('who', this.#state.answers.who),
      this.#answerLabel('periodProofExperience', this.#state.answers.periodProofExperience),
      this.#answerLabel('teenAge', this.#state.answers.teenAge),
      this.#answerLabel('teenExperience', this.#state.answers.teenExperience),
      this.#answerLabel('swimActivity', this.#state.answers.swimActivity),
      this.#answerLabel('swimStyle', this.#state.answers.swimStyle),
    ].filter(Boolean);
  }

  /**
   * @param {string} audience
   * @returns {string}
   */
  #audienceLabel(audience) {
    if (audience === 'teen') return this.#string('whoTeenLabel');

    return this.#string('whoAdultLabel');
  }

  /**
   * @param {string} activity
   * @returns {string}
   */
  #activityLabel(activity) {
    if (activity === 'competition') return this.#string('activityCompetitionLabel');

    return this.#string('activityLeisureLabel');
  }

  /**
   * @param {string} style
   * @returns {string}
   */
  #styleLabel(style) {
    const labels = {
      bikini: this.#string('styleBikiniLabel'),
      highwaist: this.#string('styleHighWaistLabel'),
      legsuit: this.#string('styleLegsuitLabel'),
      racerback: this.#string('styleRacerbackLabel'),
      scoopback: this.#string('styleScoopbackLabel'),
      shorts: this.#string('styleShortsLabel'),
    };

    return labels[style] || style;
  }

  /**
   * @param {string} changedKey
   */
  #clearAnswersAfter(changedKey) {
    const downstreamAnswers = {
      who: ['periodProofExperience', 'teenAge', 'teenExperience', 'swimActivity', 'swimStyle'],
      periodProofExperience: ['swimActivity', 'swimStyle'],
      teenAge: ['teenExperience', 'swimActivity', 'swimStyle'],
      teenExperience: ['swimActivity', 'swimStyle'],
      swimActivity: ['swimStyle'],
      swimStyle: [],
    }[changedKey];

    if (!downstreamAnswers) return;

    for (const key of downstreamAnswers) {
      delete this.#state.answers[key];
    }

    this.#clearSelectedHandles();
  }

  #clearSelectedHandles() {
    for (const key of Object.keys(this.#state.answers)) {
      if (key.startsWith('selectedHandle:') || key.startsWith('slotBase:')) delete this.#state.answers[key];
    }
  }

  /**
   * @param {string} key
   * @param {string | undefined} value
   * @returns {string}
   */
  #answerLabel(key, value) {
    if (!value) return '';

    const option = this.#optionsForStep(key).find((item) => item.value === value);

    return option?.label || '';
  }

  /**
   * @param {string} html
   * @param {boolean} [focusHeading]
   */
  #setContent(html, focusHeading = false) {
    this.refs.mount.innerHTML = html;

    if (!focusHeading) return;

    requestAnimationFrame(() => {
      const heading = this.querySelector('.period-finder__step-heading');

      if (heading instanceof HTMLElement) heading.focus();
    });
  }

  /**
   * @returns {string}
   */
  #formatProductCount() {
    if (this.#products.length === 1) return this.#string('productCountOne');

    return this.#string('productCountOther').replace('[[count]]', this.#products.length.toString());
  }

  /**
   * @param {HTMLScriptElement | undefined} element
   * @param {any} fallback
   * @returns {any}
   */
  #parseJson(element, fallback) {
    if (!(element instanceof HTMLScriptElement)) return fallback;

    try {
      return JSON.parse(element.textContent || '');
    } catch (error) {
      console.error('Period finder JSON parse error', error);
      return fallback;
    }
  }

  /**
   * @param {any} value
   * @returns {string}
   */
  #normalize(value) {
    if (Array.isArray(value)) return value.map((item) => this.#normalize(item)).join(' ');
    if (value && typeof value === 'object') return Object.values(value).map((item) => this.#normalize(item)).join(' ');

    return String(value || '').toLowerCase();
  }

  /**
   * @param {any} value
   * @returns {string}
   */
  #stripHtml(value) {
    return String(value || '').replace(/<[^>]*>/g, '');
  }

  /**
   * @param {string} key
   * @returns {string}
   */
  #string(key) {
    const strings = this.#config.strings || {};
    const value = strings[key];

    return typeof value === 'string' ? value : '';
  }

  /**
   * @param {any} value
   * @returns {string}
   */
  #escapeHtml(value) {
    const string = String(value || '');
    const replacements = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };

    return string.replace(/[&<>"']/g, (character) => replacements[character]);
  }

  /**
   * @param {any} value
   * @returns {string}
   */
  #escapeAttribute(value) {
    return this.#escapeHtml(value);
  }
}

if (!customElements.get('period-finder')) {
  customElements.define('period-finder', PeriodFinder);
}
